import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Bell,
  CheckCheck,
  UserPlus,
  Clock,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Loader2,
  ShieldCheck,
  Building2,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  NotificationRole,
  RealtimeNotification,
  useNotificationsAdvanced,
} from "../hooks/useNotifications";
import { usePushToken } from "../hooks/usePushToken";
import {
  classifyNotification,
  isNotificationRead,
  resolveNotificationTarget,
  CANCELED_STATUSES,
  timeAgo as sharedTimeAgo,
  formatTimestamp as sharedFormatTimestamp,
  summarizeNotificationDates,
  splitNotificationShiftDates,
} from "../utils/notificationDisplay";
import { Suspense, lazy } from "react";
import ApplicantRelationshipBadge from "./ApplicantRelationshipBadge";
const ShiftDetailsPopup = lazy(() => import("./ShiftDetailsPopup"));
interface NotificationPanelProps {
  role?: NotificationRole;
  userId?: string;
  onNotificationCountChange?: (count: number) => void;
  triggerClassName?: string;
}

const MAX_PREVIEW_MESSAGE = 160;

const formatTimestamp = sharedFormatTimestamp;
const timeAgo = sharedTimeAgo;

const truncate = (text: string) => {
  if (!text || text.length <= MAX_PREVIEW_MESSAGE) {
    return text;
  }
  return `${text.slice(0, MAX_PREVIEW_MESSAGE)}…`;
};

export const analyzeNotificationDate = (notification: RealtimeNotification) => {
  let isAllPast = false;
  let targetDate: string | undefined = undefined;
  let targetShiftDayId: string | undefined = undefined;

  const getTs = (ts?: number) => {
    if (!ts) return 0;
    return ts > 10000000000 ? ts : ts * 1000;
  };

  if (
    notification.appliedShiftDays &&
    notification.appliedShiftDays.length > 0
  ) {
    const todayMillis = new Date().setHours(0, 0, 0, 0);
    const sortedDays = [...notification.appliedShiftDays].sort(
      (a, b) => getTs(a.startTime) - getTs(b.startTime),
    );
    const pendingFuture = sortedDays.filter((d) => {
      const st = String(d.status || "").toLowerCase();
      return (
        getTs(d.startTime) >= todayMillis &&
        !st.includes("cancel") &&
        !st.includes("confirm")
      );
    });
    const futureDays =
      pendingFuture.length > 0
        ? pendingFuture
        : sortedDays.filter((d) => getTs(d.startTime) >= todayMillis);

    if (futureDays.length > 0) {
      targetDate = futureDays[0].shiftDate;
      targetShiftDayId = futureDays[0].shiftsDaysId;
    } else {
      isAllPast = true;
    }
  } else if (notification.shiftDate) {
    // Dates look like "Mon, 02 Sep 2025 ..." — never split on bare commas.
    const parts = splitNotificationShiftDates(notification.shiftDate);
    if (parts.length > 0) {
      targetDate = parts[0];
    }
  }

  return { isAllPast, targetDate, targetShiftDayId };
};

const NotificationItem = ({
  notification,
  onClick,
}: {
  notification: RealtimeNotification;
  onClick: (targetDate?: string, targetShiftDayId?: string) => void;
}) => {
  // Determine notification type for unique styling (shared classifier)
  const {
    isActuallyCanceled,
    isAlert,
    isPreCredential,
    isNewApplication,
    isEmploymentClaim,
    isShiftApproaching,
  } = classifyNotification(notification);
  const read = isNotificationRead(notification);

  // Get icon and colors based on type
  const getTypeStyles = () => {
    if (isPreCredential) {
      return {
        icon: <ShieldCheck className="w-5 h-5 text-indigo-600" />,
        bgColor: read ? "bg-white" : "bg-indigo-50",
        borderColor: "border-l-4 border-l-indigo-500",
        badgeColor: "bg-indigo-100 text-indigo-700 border-indigo-300",
      };
    }
    if (isEmploymentClaim) {
      return {
        icon: <Building2 className="w-5 h-5 text-amber-700" />,
        bgColor: read ? "bg-white" : "bg-amber-50",
        borderColor: "border-l-4 border-l-amber-500",
        badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
      };
    }
    if (isNewApplication) {
      return {
        icon: <UserPlus className="w-5 h-5 text-green-600" />,
        bgColor: read ? "bg-white" : "bg-green-50",
        borderColor: "border-l-4 border-l-green-500",
        badgeColor: "bg-green-100 text-green-700 border-green-300",
      };
    }
    if (isShiftApproaching) {
      return {
        icon: <Clock className="w-5 h-5 text-amber-600" />,
        bgColor: read ? "bg-white" : "bg-amber-50",
        borderColor: "border-l-4 border-l-amber-500",
        badgeColor: "bg-amber-100 text-amber-700 border-amber-300",
      };
    }
    if (isAlert) {
      return {
        icon: <AlertTriangle className="w-5 h-5 text-red-600" />,
        bgColor: read ? "bg-white" : "bg-red-50",
        borderColor: "border-l-4 border-l-red-500",
        badgeColor: "bg-red-100 text-red-700 border-red-300",
      };
    }
    return {
      icon: <Bell className="w-5 h-5 text-primary" />,
      bgColor: read ? "bg-white" : "bg-blue-50",
      borderColor: "border-l-4 border-l-primary",
      badgeColor: "bg-primary/10 text-primary border-primary/30",
    };
  };

  const styles = getTypeStyles();
  const hasDoctor = !!notification.doctorName;
  const hasShift = !!notification.shiftNo;

  const { isAllPast, targetDate, targetShiftDayId } =
    analyzeNotificationDate(notification);

  return (
    <div
      onClick={
        isAllPast ? undefined : () => onClick(targetDate, targetShiftDayId)
      }
      className={`px-4 py-3 ${styles.bgColor} ${!isAllPast ? "hover:bg-gray-100 cursor-pointer" : "opacity-60 cursor-not-allowed"} transition-colors border-b border-gray-100 last:border-0`}
    >
      <div className="flex gap-3 relative">
        {/* Avatar or Icon */}
        <div className="flex-shrink-0 mt-1">
          {hasDoctor && notification.image ? (
            <img
              src={notification.image}
              alt={notification.doctorName}
              className="w-10 h-10 rounded-full object-cover border border-gray-200 shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${styles.badgeColor.split(" ")[0]}`}
            >
              {styles.icon}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div className="font-semibold text-sm text-gray-900 pr-2 flex items-center flex-wrap gap-1.5">
              <span>
                {hasDoctor ? notification.doctorName : notification.title}
              </span>
              {isActuallyCanceled && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 border border-red-300">
                  Canceled
                </span>
              )}
              {isNewApplication && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-300">
                  New Application
                </span>
              )}
              {isPreCredential && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 border border-indigo-300">
                  Pre-Credential
                </span>
              )}
              {isEmploymentClaim && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300">
                  Asked to attach
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5">
              {timeAgo(notification.timestamp)}
            </span>
          </div>

          {(!isNewApplication ||
            (notification.message &&
              !notification.message
                .toLowerCase()
                .includes("has applied for your shift") &&
              !notification.title
                .toLowerCase()
                .includes("new application received"))) && (
            <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">
              {notification.message}
            </div>
          )}

          {/* Context Badges */}
          <div className="flex flex-col gap-1.5 mt-2">
            {notification.shiftDate && (
              <div className="flex items-start px-1.5 py-1 rounded text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-200 w-full">
                <Calendar className="w-3 h-3 mr-1 mt-[1px] flex-shrink-0 text-gray-400" />
                <span
                  className="leading-[14px] line-clamp-2"
                  title={notification.shiftDate}
                >
                  {summarizeNotificationDates(notification).label ||
                    notification.shiftDate}
                </span>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              {(() => {
                let activeDaysCount = 0;
                if (
                  notification.appliedShiftDays &&
                  notification.appliedShiftDays.length > 0
                ) {
                  const tMillis = new Date().setHours(0, 0, 0, 0);
                  const futureActive = notification.appliedShiftDays.filter(
                    (d) => {
                      const st = String(d.status || "");
                      const stLower = st.toLowerCase();
                      const ts =
                        d.startTime && d.startTime > 10000000000
                          ? d.startTime
                          : d.startTime
                            ? d.startTime * 1000
                            : 0;
                      return (
                        ts >= tMillis &&
                        !CANCELED_STATUSES.includes(st) &&
                        !stLower.includes("cancel") &&
                        !stLower.includes("confirm")
                      );
                    },
                  );
                  activeDaysCount = futureActive.length;
                } else {
                  activeDaysCount =
                    summarizeNotificationDates(notification).count;
                }
                if (activeDaysCount > 1) {
                  return (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-100"
                      title="Multiple Days"
                    >
                      {activeDaysCount} Days
                    </span>
                  );
                }
                return null;
              })()}
              <ApplicantRelationshipBadge applicant={notification} />
              {notification.shiftSpecialtyName && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 max-w-[120px] truncate"
                  title={notification.shiftSpecialtyName}
                >
                  {notification.shiftSpecialtyName}
                </span>
              )}
              {notification.shiftSkillLevelName && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-50 text-teal-700 border border-teal-200 max-w-[120px] truncate"
                  title={notification.shiftSkillLevelName}
                >
                  {notification.shiftSkillLevelName}
                </span>
              )}
              {!notification.shiftSpecialtyName &&
                hasDoctor &&
                notification.doctorSpecialty && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100 max-w-[120px] truncate">
                    {notification.doctorSpecialty.split(",")[0]}
                  </span>
                )}
              {notification.messageEventType && !isNewApplication && (
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${styles.badgeColor}`}
                >
                  {notification.messageEventType.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Unread Indicator */}
        {!read && (
          <div className="flex-shrink-0 mt-2">
            <span className="block w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse shadow-sm ring-2 ring-white"></span>
          </div>
        )}
      </div>
    </div>
  );
};

const NotificationPanel = ({
  role = "hospital",
  userId,
  onNotificationCountChange,
  triggerClassName,
}: NotificationPanelProps) => {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [selectedShiftForModal, setSelectedShiftForModal] = useState<any>(null);
  const navigate = useNavigate();

  // Initialize push notifications
  const { permission, requestPermission } = usePushToken();

  const hospitalFromStore = useSelector(
    (state: any) => state.currentHospital?.current,
  );

  const resolvedUserId = userId ?? hospitalFromStore?.id ?? null;

  // STRICT SINGLE HOSPITAL MODE:
  // User requested that meaningful notifications should ONLY be for the currently selected hospital.
  // Previous logic aggregated notifications for master accounts, but this was confusing.
  const networkHospitalIds = undefined;

  // Pass networkHospitalIds as undefined to ensure we only get notifications for the specific resolvedUserId (current hospital)
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch,
    loadOlder,
    hasLoadedOlder,
  } = useNotificationsAdvanced(role, resolvedUserId, networkHospitalIds);

  // Format count as two digits (e.g., "01", "09", "99+")
  const formatCount = (count: number): string => {
    if (count > 99) return "99+";
    return count.toString().padStart(2, "0");
  };

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    // Only block if count is 0. Do NOT block if 'markingAsRead' is true,
    // because we might want to re-try or the user might be aggressive.
    // Actually, blocking on markingAsRead is good to prevent double API calls, but
    // we should ensure the UI updates instantly.
    if (unreadCount === 0) return;

    setMarkingAsRead(true);

    // FIRE AND FORGET (Optimistic UI):
    // 1. We don't wait for the result to update the UI if we could control the hook state,
    //    but since we can't easily reach into the hook's state deeply without refactoring,
    //    we rely on the hook's own optimistic update.
    //    HOWEVER, the user says "it's not working at all". This suggests the API call might be failing silently
    //    or the condition to run this function isn't met.

    console.log("🔔 Triggering Mark All As Read...");

    await markAllAsRead();

    setMarkingAsRead(false);
  }, [unreadCount, markAllAsRead]);

  // Handle notification click - navigate to relevant page
  const handleNotificationClick = useCallback(
    (
      notification: RealtimeNotification,
      targetDate?: string,
      targetShiftDayId?: string,
    ) => {
      setOpen(false);

      // Update read status immediately
      markAsRead(notification.activityId);

      const target = resolveNotificationTarget(notification, {
        targetDate,
        targetShiftDayId,
      });

      switch (target.kind) {
        case "shift-modal":
          if (!target.shiftsId) break;
          setSelectedShiftForModal({
            shiftsId: target.shiftsId,
            shiftId: target.shiftsId,
            shiftsDaysId: target.shiftsDaysId || undefined,
            shiftDayId: target.shiftsDaysId || undefined,
            openApplicantUserId: target.openApplicantUserId,
            ordersId: target.ordersId,
            openApplicantsTab: target.openApplicantsTab,
          });
          break;
        case "calendar":
          navigate("/calendar", {
            state: {
              openShiftId: target.openShiftId,
              shiftDate: target.shiftDate,
            },
          });
          break;
        case "route":
          navigate(target.to);
          break;
        case "none":
        default:
          break;
      }
    },
    [navigate, markAsRead],
  );

  useEffect(() => {
    if (onNotificationCountChange) {
      onNotificationCountChange(unreadCount);
    }
  }, [unreadCount, onNotificationCountChange]);

  const disabled = !resolvedUserId;
  // Reset visible count when opening panel
  useEffect(() => {
    if (open) {
      setVisibleCount(10);
      // We do NOT auto-mark as read here anymore, so unread notifications
      // don't instantly vanish before the user can see them.
    }
  }, [open, disabled]);

  const filteredNotifications = notifications.filter((n) => {
    // Always show unread — even if the related shift date is in the past.
    if (!n.isRead) return true;

    let isPast = analyzeNotificationDate(n).isAllPast;
    const isRecent = Date.now() - n.timestamp < 3 * 24 * 60 * 60 * 1000; // 3 days

    if (
      !n.shiftDate &&
      (!n.appliedShiftDays || n.appliedShiftDays.length === 0)
    ) {
      isPast = !isRecent;
    }

    return !isPast;
  });

  // Empty inbox + sticky badge: load full allowlist so list cleanup reconciles
  // the shared hospital unread count (dropdown chips).
  useEffect(() => {
    if (disabled || loading || hasLoadedOlder) return;
    if (filteredNotifications.length > 0) return;
    if (unreadCount <= 0) return;
    loadOlder();
  }, [
    disabled,
    loading,
    hasLoadedOlder,
    filteredNotifications.length,
    unreadCount,
    loadOlder,
  ]);

  return (
    <>
      <Suspense fallback={null}>
        <ShiftDetailsPopup
          isOpen={!!selectedShiftForModal}
          shift={selectedShiftForModal}
          onClose={() => setSelectedShiftForModal(null)}
          onShiftChange={(newShift) => setSelectedShiftForModal(newShift)}
        />
      </Suspense>
      <Popover
        open={open && !disabled}
        onOpenChange={(value) => {
          if (disabled) return;
          setOpen(value);
          if (value && permission === "default") {
            requestPermission();
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            onClick={() => {
              if (permission === "default") {
                requestPermission();
              }
            }}
            className={cn(
              "relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50",
              triggerClassName,
            )}
            disabled={disabled}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
                {formatCount(unreadCount)}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-96 p-0 bg-white rounded-xl shadow-2xl border border-border flex flex-col max-h-[600px] overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {formatCount(unreadCount)} new
                </span>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  refetch();
                }}
                disabled={loading}
                className={`ml-2 p-1 rounded-full text-muted-foreground hover:bg-gray-100 transition-all ${loading ? "animate-spin" : "hover:text-primary"}`}
                title="Refresh notifications"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markingAsRead}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                {markingAsRead ? "Marking..." : "Mark all read"}
              </button>
            )}
          </div>

          <div
            className="flex-1 overflow-y-auto min-h-0 relative"
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (
                target.scrollHeight - target.scrollTop <=
                target.clientHeight + 50
              ) {
                if (visibleCount < filteredNotifications.length) {
                  // Just reveal more local items
                  setVisibleCount((prev) => prev + 10);
                } else if (!hasLoadedOlder && !isFetchingMore) {
                  setIsFetchingMore(true);
                  loadOlder();
                  setTimeout(() => setIsFetchingMore(false), 800);
                }
              }
            }}
          >
            {disabled ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-6 text-sm text-muted-foreground">
                Link a hospital account to enable notifications.
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-6 text-sm text-muted-foreground">
                <Bell className="w-12 h-12 text-gray-300 mb-3" />
                {unreadCount > 0 ? (
                  <>
                    <p className="font-medium text-foreground mb-1">
                      Syncing inbox…
                    </p>
                    <p className="text-xs leading-relaxed max-w-[260px]">
                      Clearing stale badge counts. If this persists, click
                      “Mark all read”.
                    </p>
                  </>
                ) : (
                  "No active notifications"
                )}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredNotifications
                  .slice(0, visibleCount)
                  .map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onClick={(targetDate, targetShiftDayId) =>
                        handleNotificationClick(
                          notification,
                          targetDate,
                          targetShiftDayId,
                        )
                      }
                    />
                  ))}
                {isFetchingMore && !hasLoadedOlder && (
                  <div className="flex flex-col items-center justify-center p-4 text-xs font-medium bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-primary">
                        Fetching older notifications...
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      Loading historical data
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default NotificationPanel;
